import React from "react";
import Link from "next/link";
import "../../css/KeyFeatures.css";
import BrandName from "../BrandName";
import {
  getKeyFeaturePagesByCategory,
  type KeyFeaturePage,
} from "@/lib/keyFeaturePages";
import {
  AlertCircle,
  Search,
  Users,
  BarChart,
  Stethoscope,
  Shield,
  Syringe,
  TrendingUp,
  Building,
  BookOpen,
  Route,
  ClipboardCheck,
  MessageSquare,
  FileSearch,
  PieChart,
  ChevronRight,
  ShieldCheck,
  FolderOpen,
  HeartPulse,
  Siren,
  type LucideIcon,
} from "lucide-react";

const KEY_FEATURE_ICONS: Record<string, LucideIcon> = {
  "/features/committee-meeting-management": Users,
  "/features/performance-improvement-plans": TrendingUp,
  "/features/incident-investigation": Search,
  "/features/grievance-management": AlertCircle,
  "/features/medical-directorship-engagement-compliance": Stethoscope,
  "/features/action-plans": ClipboardCheck,
  "/features/audit-management": ShieldCheck,
  "/features/facility-assessment": Building,
  "/features/critical-element-pathways": Route,
  "/features/mock-surveys": FileSearch,
  "/features/survey-management": PieChart,
  "/features/osha-respiratory-protection": Shield,
  "/features/vaccination-tracking-management": Syringe,
  "/features/metric-task-action-plans": BarChart,
  "/features/policy-and-procedure-management": BookOpen,
  "/features/document-vault": FolderOpen,
  "/features/feedback-and-satisfaction-surveys": MessageSquare,
  "/features/emergency-preparedness": Siren,
};

function toFeatureLink(page: KeyFeaturePage) {
  const Icon = KEY_FEATURE_ICONS[page.feature_menu_url] ?? ClipboardCheck;
  return {
    title: page.label,
    link: page.feature_menu_url,
    icon: Icon,
  };
}

const qapiFeatures = getKeyFeaturePagesByCategory("qapi").map(toFeatureLink);
const healthFeatures = getKeyFeaturePagesByCategory("health").map(toFeatureLink);
const operationsFeatures = getKeyFeaturePagesByCategory("operations").map(toFeatureLink);

const KeyFeatures = () => {
  return (
    <section className="key-features">
      <div className="kf-header">
        <h2>KEY FEATURES</h2>
        <p>Everything you need to manage quality, employee health, and operational excellence.</p>
      </div>

      <div className="key-features-container">
        <div className="kf-architecture-grid key-features-grid">
        {/* Card 1: QAPI */}
        <div className="kf-arch-card kf-arch-card-left qapi-card side-card">
          <div className="kf-top-badge kf-badge-green">
            <ClipboardCheck size={28} />
          </div>
          <div className="kf-arch-card-header">
            <div className="kf-arch-illustration kf-illust-qapi">
              <img 
                src="/assets/key-features/qapi-quality-management.png" 
                alt="QAPI Quality Management" 
                className="feature-card-image"
                style={{ width: '100%', objectFit: 'contain', transform: 'scale(1.35)' }} 
              />
            </div>
            <div className="kf-arch-body feature-card-content">
              <h3 className="feature-card-title">QAPI & Quality Management</h3>
              <p className="feature-card-description">Manage assessments, audits, grievances, surveys, action plans, and quality improvement from a single platform.</p>
            </div>
          </div>
          <div className="kf-arch-divider text-green">
            <span>FEATURES INSIDE</span>
          </div>
          <div className="kf-arch-list">
            {qapiFeatures.map((f) => (
              <Link href={f.link} key={f.link} className="kf-list-item feature-link">
                <f.icon className="kf-list-icon text-green" size={20} />
                <span className="kf-list-title">{f.title}</span>
                <ChevronRight className="kf-list-chevron" size={16} />
              </Link>
            ))}
          </div>
        </div>

        <div className="right-feature-column">
          {/* Card 2: Health */}
          <div className="kf-arch-card kf-arch-card-right health-card side-card">
            <div className="kf-top-badge kf-badge-blue">
              <HeartPulse size={28} />
            </div>
            <div className="kf-arch-card-header">
              <div className="kf-arch-illustration kf-illust-health">
                <img 
                  src="/assets/key-features/employee-health-v2.png" 
                  alt="Employee Health" 
                  className="feature-card-image"
                  style={{ width: '100%', objectFit: 'contain' }} 
                />
              </div>
              <div className="kf-arch-body feature-card-content">
                <h3 className="feature-card-title">Employee Health</h3>
                <p className="feature-card-description">Track vaccinations, employee health requirements, and OSHA compliance across every facility.</p>
              </div>
            </div>
            <div className="kf-arch-divider text-blue">
              <span>FEATURES INSIDE</span>
            </div>
            <div className="kf-arch-list">
              {healthFeatures.map((f) => (
                <Link href={f.link} key={f.link} className="kf-list-item feature-link">
                  <f.icon className="kf-list-icon text-blue" size={20} />
                  <span className="kf-list-title">{f.title}</span>
                  <ChevronRight className="kf-list-chevron" size={16} />
                </Link>
              ))}
            </div>
          </div>

          {/* Card 3: Operations */}
          <div className="kf-arch-card kf-arch-card-right ops-card side-card">
            <div className="kf-top-badge kf-badge-lavender">
              <TrendingUp size={28} />
            </div>
            <div className="kf-arch-card-header">
              <div className="kf-arch-illustration kf-illust-ops">
                <img 
                  src="/assets/key-features/operational-efficiency.png" 
                  alt="Operational Efficiency" 
                  className="feature-card-image"
                  style={{ width: '100%', objectFit: 'contain', transform: 'scale(1.15)' }} 
                />
              </div>
              <div className="kf-arch-body feature-card-content">
                <h3 className="feature-card-title">Operational Efficiency & Insights</h3>
                <p className="feature-card-description">Automate recurring workflows, policies, metrics, and reporting with real-time visibility.</p>
              </div>
            </div>
            <div className="kf-arch-divider text-green">
              <span>FEATURES INSIDE</span>
            </div>
            <div className="kf-arch-list">
              {operationsFeatures.map((f) => (
                <Link href={f.link} key={f.link} className="kf-list-item feature-link">
                  <f.icon className="kf-list-icon text-green" size={20} />
                  <span className="kf-list-title">{f.title}</span>
                  <ChevronRight className="kf-list-chevron" size={16} />
                </Link>
              ))}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Footer Strip */}
      <div className="key-features-bottom-strip">
        <div className="key-features-bottom-strip-text">
          <BrandName variant="white" style={{ fontSize: 'inherit' }} /> brings it all together so you can focus on what matters most—your residents and your team.
        </div>
        <img 
          src="/footer-teamwork-illust.png" 
          alt="Team Work" 
          className="key-features-bottom-strip-image"
        />
      </div>
    </section>
  );
};

export default KeyFeatures;
